// src/components/admin/ProjectManager.jsx
import React, { useState, useEffect } from 'react';
import { getProjects, createProject, updateProject, deleteProject } from '../../utils/api';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom'; // Add this import
const ProjectManager = () => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        client: '',
        location: '',
        start_date: '',
        end_date: '',
        status: 'completed',
        category: 'General', // Added category field
        image_url: '',
        image_file: null,
        is_active: true,
        is_featured: false, // Added is_featured field
        details: {
            scope: '',
            challenges: '',
            solutions: '',
            results: ''
        }
    });

    // Your Railway file server URL
    const FILE_SERVER_URL = 'https://nkumbufileserver-production.up.railway.app';

    const statusOptions = [
        { value: 'planning', label: 'Planning' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'completed', label: 'Completed' },
        { value: 'on_hold', label: 'On Hold' }
    ];

    useEffect(() => {
        loadProjects();
    }, []);

    const loadProjects = async () => {
        try {
            setLoading(true);
            const data = await getProjects();
            
            // Handle different response structures
            if (Array.isArray(data)) {
                setProjects(data);
            } else if (data && data.data && Array.isArray(data.data)) {
                setProjects(data.data);
            } else {
                setProjects([]);
            }
        } catch (error) {
            console.error('Error loading projects:', error);
            toast.error('Failed to load projects');
            setProjects([]);
        } finally {
            setLoading(false);
        }
    };

    // Upload file to Railway file server
    const uploadToFileServer = async (file) => {
        setUploading(true);
        setUploadProgress(0);
        
        try {
            const formData = new FormData();
            formData.append('file', file);

            // Create a new XMLHttpRequest for progress tracking
            return new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                
                xhr.upload.addEventListener('progress', (event) => {
                    if (event.lengthComputable) {
                        const percentComplete = (event.loaded / event.total) * 100;
                        setUploadProgress(Math.round(percentComplete));
                    }
                });

                xhr.addEventListener('load', () => {
                    if (xhr.status === 200) {
                        const response = JSON.parse(xhr.responseText);
                        setUploading(false);
                        setUploadProgress(0);
                        resolve(response.fileInfo.fileUrl);
                    } else {
                        reject(new Error(`Upload failed with status ${xhr.status}`));
                    }
                });

                xhr.addEventListener('error', () => {
                    reject(new Error('Upload failed'));
                });

                xhr.open('POST', `${FILE_SERVER_URL}/upload`);
                xhr.send(formData);
            });
        } catch (error) {
            setUploading(false);
            setUploadProgress(0);
            console.error('Upload error:', error);
            throw error;
        }
    };

    // Handle file selection
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
    
        // Validate file size (max 100MB)
        const maxSize = 100 * 1024 * 1024; // 100MB in bytes
        if (file.size > maxSize) {
            toast.error('File size exceeds 100MB limit');
            return;
        }
    
        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Please select an image file (JPEG, PNG, GIF, WebP, SVG)');
            return;
        }
    
        // Create preview URL
        const previewUrl = URL.createObjectURL(file);
        
        setFormData(prev => ({
            ...prev,
            image_file: file,
            image_url: previewUrl // This is just a preview, not the final URL
        }));
        
        console.log('File selected:', file.name, 'Preview URL:', previewUrl);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            let imageUrl = formData.image_url;
    
            // If a new file is selected, upload it to the file server
            if (formData.image_file) {
                toast.loading('Uploading image to file server...');
                imageUrl = await uploadToFileServer(formData.image_file);
                toast.dismiss();
                toast.success('Image uploaded successfully!');
                
                // Update formData with the new URL
                setFormData(prev => ({
                    ...prev,
                    image_url: imageUrl
                }));
            }
    
            // Prepare project data - send images as array
            const projectData = {
                title: formData.title,
                description: formData.description,
                client_name: formData.client,
                location: formData.location,
                // Transform dates to ISO format
                start_date: formData.start_date ? `${formData.start_date}T00:00:00Z` : null,
                end_date: formData.end_date ? `${formData.end_date}T00:00:00Z` : null,
                status: formData.status,
                category: formData.category || 'General',
                // Send as array - use the updated imageUrl
                images: imageUrl ? [imageUrl] : [],
                videos: [], // Always send empty array for videos
                is_active: formData.is_active,
                is_featured: formData.is_featured,
                details: formData.details
            };
    
            console.log('Sending project data:', projectData);
    
            if (editingId) {
                // Update existing project
                await updateProject(editingId, projectData);
                toast.success('Project updated successfully!');
            } else {
                // Create new project
                await createProject(projectData);
                toast.success('Project added successfully!');
            }
            
            resetForm();
            await loadProjects();
            
        } catch (error) {
            console.error('Error saving project:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to save project';
            toast.error(errorMessage);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this project?')) {
            try {
                await deleteProject(id);
                toast.success('Project deleted successfully!');
                await loadProjects();
            } catch (error) {
                console.error('Error deleting project:', error);
                toast.error('Failed to delete project');
            }
        }
    };

    const handleEdit = (project) => {
        setFormData({
            title: project.title || '',
            description: project.description || '',
            client: project.client || project.client_name || '',
            location: project.location || '',
            start_date: project.start_date?.split('T')[0] || '',
            end_date: project.end_date?.split('T')[0] || '',
            status: project.status || 'completed',
            category: project.category || 'General',
            // Get first image from images array
            image_url: project.images && project.images.length > 0 ? project.images[0] : '',
            image_file: null,
            is_active: project.is_active !== undefined ? project.is_active : true,
            is_featured: project.is_featured || false,
            details: project.details || {
                scope: '',
                challenges: '',
                solutions: '',
                results: ''
            }
        });
        setEditingId(project.id);
        setShowForm(true);
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            client: '',
            location: '',
            start_date: '',
            end_date: '',
            status: 'completed',
            category: 'General',
            image_url: '',
            image_file: null,
            is_active: true,
            is_featured: false,
            details: {
                scope: '',
                challenges: '',
                solutions: '',
                results: ''
            }
        });
        setEditingId(null);
        setShowForm(false);
        setUploadProgress(0);
        setUploading(false);
    };

    const handleToggleActive = async (id, currentStatus) => {
        try {
            await updateProject(id, { is_active: !currentStatus });
            toast.success(`Project ${!currentStatus ? 'activated' : 'deactivated'}`);
            await loadProjects();
        } catch (error) {
            console.error('Error toggling project status:', error);
            toast.error('Failed to update project status');
        }
    };

    const handleClearImage = () => {
        setFormData(prev => ({
            ...prev,
            image_file: null,
            image_url: ''
        }));
    };

    const getStatusColor = (status) => {
        switch(status) {
            case 'completed': return 'bg-green-100 text-green-800';
            case 'in_progress': return 'bg-blue-100 text-blue-800';
            case 'planning': return 'bg-yellow-100 text-yellow-800';
            case 'on_hold': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const formatStatus = (status) => {
        return statusOptions.find(opt => opt.value === status)?.label || status;
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Projects</h2>
                <button
                    onClick={() => {
                        resetForm();
                        setShowForm(true);
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                    Add New Project
                </button>
            </div>

            {showForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white p-6 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold">
                                {editingId ? 'Edit Project' : 'Add New Project'}
                            </h3>
                            <button
                                onClick={resetForm}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ✕
                            </button>
                        </div>
                        
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Project Title *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                        disabled={uploading}
                                    />
                                </div>
                                
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Description *
                                    </label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        rows="3"
                                        required
                                        disabled={uploading}
                                    />
                                </div>

                                {/* Add Category Field */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Category
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.category}
                                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        disabled={uploading}
                                        placeholder="e.g., Construction, Renovation, Design"
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Client
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.client}
                                        onChange={(e) => setFormData({...formData, client: e.target.value})}
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        disabled={uploading}
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Location
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.location}
                                        onChange={(e) => setFormData({...formData, location: e.target.value})}
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        disabled={uploading}
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Start Date
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.start_date}
                                        onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        disabled={uploading}
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        End Date
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.end_date}
                                        onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        disabled={uploading}
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Status
                                    </label>
                                    <select
                                        value={formData.status}
                                        onChange={(e) => setFormData({...formData, status: e.target.value})}
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        disabled={uploading}
                                    >
                                        {statusOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Project Image
                                    </label>
                                    
                                    {/* Upload Section */}
                                    <div className="space-y-4">
                                        {/* File Upload */}
                                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                                            <input
                                                type="file"
                                                id="image-upload"
                                                onChange={handleFileSelect}
                                                accept="image/*"
                                                className="hidden"
                                                disabled={uploading}
                                            />
                                            <label
                                                htmlFor="image-upload"
                                                className={`cursor-pointer ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            >
                                                <div className="flex flex-col items-center">
                                                    <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                                                    </svg>
                                                    <p className="text-sm text-gray-600 mb-2">
                                                        <span className="font-semibold">Click to upload</span> or drag and drop
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        PNG, JPG, GIF, WebP, SVG up to 100MB
                                                    </p>
                                                </div>
                                            </label>
                                        </div>

                                        {/* Upload Progress */}
                                        {uploading && (
                                            <div className="w-full">
                                                <div className="flex justify-between text-sm text-gray-600 mb-1">
                                                    <span>Uploading...</span>
                                                    <span>{uploadProgress}%</span>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-2">
                                                    <div 
                                                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                                        style={{ width: `${uploadProgress}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Image Preview */}
                                        {formData.image_url && (
                                            <div className="relative">
                                                <p className="text-sm text-gray-600 mb-2">Preview:</p>
                                                <div className="relative inline-block">
                                                    <img 
                                                        src={formData.image_url} 
                                                        alt="Preview"
                                                        className="max-w-full h-48 object-cover rounded border"
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = '/default-project.jpg';
                                                        }}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={handleClearImage}
                                                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                                        disabled={uploading}
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Current Image URL (for editing) */}
                                        {editingId && formData.image_url && !formData.image_file && (
                                            <div>
                                                <p className="text-sm text-gray-600 mb-1">Current Image URL:</p>
                                                <input
                                                    type="text"
                                                    value={formData.image_url}
                                                    onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                                                    className="w-full p-2 border border-gray-300 rounded text-sm"
                                                    placeholder="Or enter image URL directly"
                                                    disabled={uploading}
                                                />
                                            </div>
                                        )}

                                        {/* Direct URL Input */}
                                        <div>
                                            <p className="text-sm text-gray-600 mb-1">Or enter image URL directly:</p>
                                            <input
                                                type="url"
                                                value={formData.image_file ? '' : formData.image_url}
                                                onChange={(e) => setFormData({...formData, image_url: e.target.value, image_file: null})}
                                                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="https://example.com/project.jpg"
                                                disabled={uploading}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Project Details */}
                            <div className="border-t pt-6">
                                <h4 className="text-lg font-semibold text-gray-800 mb-4">Project Details</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Project Scope
                                        </label>
                                        <textarea
                                            value={formData.details.scope}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                details: { ...formData.details, scope: e.target.value }
                                            })}
                                            className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            rows="2"
                                            disabled={uploading}
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Challenges
                                        </label>
                                        <textarea
                                            value={formData.details.challenges}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                details: { ...formData.details, challenges: e.target.value }
                                            })}
                                            className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            rows="2"
                                            disabled={uploading}
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Solutions
                                        </label>
                                        <textarea
                                            value={formData.details.solutions}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                details: { ...formData.details, solutions: e.target.value }
                                            })}
                                            className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            rows="2"
                                            disabled={uploading}
                                        />
                                    </div>
                                    
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Results
                                        </label>
                                        <textarea
                                            value={formData.details.results}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                details: { ...formData.details, results: e.target.value }
                                            })}
                                            className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            rows="2"
                                            disabled={uploading}
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="is_active"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                                    className="rounded text-blue-600 focus:ring-blue-500"
                                    disabled={uploading}
                                />
                                <label htmlFor="is_active" className="text-sm text-gray-700">
                                    Active
                                </label>
                            </div>

                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="is_featured"
                                    checked={formData.is_featured}
                                    onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                                    className="rounded text-blue-600 focus:ring-blue-500"
                                    disabled={uploading}
                                />
                                <label htmlFor="is_featured" className="text-sm text-gray-700">
                                    Featured Project
                                </label>
                            </div>
                            
                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50"
                                    disabled={uploading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={uploading}
                                >
                                    {uploading ? (
                                        <span className="flex items-center">
                                            <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Uploading...
                                        </span>
                                    ) : editingId ? 'Update Project' : 'Add Project'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-4 text-gray-600">Loading projects...</p>
                    </div>
                </div>
            ) : projects.length === 0 ? (
                <div className="text-center py-12">
                    <p className="text-gray-500 text-lg">No projects found</p>
                    <p className="text-gray-400 mt-2">Add your first project to get started</p>
                </div>
            ) : (
                <div className="space-y-6">
                    {projects.map(project => (
                        <div key={project.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                            <div className="md:flex">
                                {/* FIXED: Use project.images array instead of project.image_url */}
                                {project.images && project.images.length > 0 && (
                                    <div className="md:w-1/3">
                                        <img 
                                            src={project.images[0]} // Get first image from array
                                            alt={project.title}
                                            className="w-full h-48 md:h-full object-cover"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = '/default-project.jpg';
                                            }}
                                        />
                                    </div>
                                )}
                                {/* FIXED: Check images array for conditional class */}
                                <div className={`p-6 ${project.images && project.images.length > 0 ? 'md:w-2/3' : 'w-full'}`}>
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <h3 className="text-xl font-bold text-gray-800 mb-1">{project.title}</h3>
                                            <div className="flex items-center space-x-3 text-sm text-gray-600">
                                                {project.category && project.category !== 'General' && (
                                                    <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                                                        {project.category}
                                                    </span>
                                                )}
                                                {project.client || project.client_name ? (
                                                    <span>Client: {project.client || project.client_name}</span>
                                                ) : null}
                                                {project.location && (
                                                    <span>Location: {project.location}</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                                                {formatStatus(project.status)}
                                            </span>
                                            {project.is_featured && (
                                                <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                    Featured
                                                </span>
                                            )}
                                            <button
                                                onClick={() => handleToggleActive(project.id, project.is_active)}
                                                className={`px-3 py-1 rounded text-xs ${
                                                    project.is_active 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : 'bg-red-100 text-red-800'
                                                }`}
                                            >
                                                {project.is_active ? 'Active' : 'Inactive'}
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <p className="text-gray-700 mb-4 line-clamp-2">{project.description}</p>
                                    
                                    <div className="flex items-center text-sm text-gray-600 mb-4">
                                        {project.start_date && (
                                            <span className="mr-4">
                                                Start: {new Date(project.start_date).toLocaleDateString()}
                                            </span>
                                        )}
                                        {project.end_date && (
                                            <span>
                                                End: {new Date(project.end_date).toLocaleDateString()}
                                            </span>
                                        )}
                                    </div>
                                    
                                    <div className="flex justify-between items-center pt-4 border-t">
                                        <button
                                            onClick={() => handleEdit(project)}
                                            className="text-blue-600 hover:text-blue-800 font-medium"
                                        >
                                            Edit
                                        </button>

                                        <Link
                                        to={`/admin/projects/${project.id}`}
                                        className="text-green-600 hover:text-green-800 text-sm font-medium"
  >
    View Details
  </Link>
                                        <div className="flex space-x-3">
                                            <button
                                                onClick={() => handleDelete(project.id)}
                                                className="text-red-600 hover:text-red-800 font-medium"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ProjectManager;